/* eslint-disable max-lines-per-function */
import { useCreateTermineFormContext } from '@/domain/termine/features/CreateTermin/shared/useCreateTermineFormContext';
import { useDrawerSelection } from '@/domain/termine/features/CreateTermin/Drawer/hooks/useDrawerSelection';
import {
  getWeekdayOccurrenceStringFromTerminWeekdayOccurrence,
  TerminWeekdayOccurrence,
  TerminWiederholungsIntervall,
} from '@/domain/termine/shared/types';
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  ScrollArea,
} from '@e2n/ui';
import { faCheck, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import { useState } from 'react';

const validIntervals = Object.values(TerminWiederholungsIntervall);

function isTerminWiederholungsIntervall(
  value: unknown,
): value is TerminWiederholungsIntervall {
  return (
    typeof value === 'string' &&
    validIntervals.some((interval) => interval === value)
  );
}

export default function WeedayOccurrenceDrawer({
  value,
  onChange,
}: {
  value: TerminWeekdayOccurrence | undefined;
  onChange: (value: TerminWeekdayOccurrence) => void;
}) {
  const { form } = useCreateTermineFormContext();
  const initialValue = value ?? TerminWeekdayOccurrence.FIXER_TAG;
  const { localSelection, handleOpenChange, handleSelect } =
    useDrawerSelection<TerminWeekdayOccurrence>(initialValue);
  const [open, setOpen] = useState(false);

  return (
    <form.Subscribe
      selector={(state) => [
        state.values.datum,
        state.values.wiederholungsIntervall,
      ]}>
      {([date, selectedRepetition]) => {
        const repetition = isTerminWiederholungsIntervall(selectedRepetition)
          ? selectedRepetition
          : TerminWiederholungsIntervall.WOCHE;

        return (
          <div className="pb-3 h-10">
            <Drawer
              open={open}
              onOpenChange={(isOpen) => {
                setOpen(isOpen);
                handleOpenChange(isOpen);
              }}>
              <DrawerTrigger asChild>
                <Button
                  variant="outline-secondary"
                  className="w-full justify-between">
                  <span className="text-sm font-normal">
                    {getWeekdayOccurrenceStringFromTerminWeekdayOccurrence(
                      localSelection,
                      date,
                      repetition,
                    )}
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="text-neutrals-400"
                  />
                </Button>
              </DrawerTrigger>

              <DrawerContent className="flex flex-col">
                <DrawerHeader className="shrink-0">
                  <DrawerTitle className="text-sm text-center text-neutrals-900 font-semibold">
                    {t('domain.termine.sheet.fields.jeden')}
                  </DrawerTitle>
                  <DrawerDescription className="sr-only">
                    {t('domain.termine.sheet.fields.jeden')}
                  </DrawerDescription>
                </DrawerHeader>
                <ScrollArea className="flex-1 p-4" data-vaul-no-drag>
                  <div className="space-y-0">
                    {Object.values(TerminWeekdayOccurrence).map(
                      (enumValue, idx) => {
                        const isSelected = localSelection === enumValue;

                        return (
                          <div key={enumValue}>
                            <Button
                              variant="ghost"
                              aria-selected={isSelected}
                              onClick={() => {
                                handleSelect(enumValue);
                                onChange(enumValue);
                                setOpen(false);
                              }}
                              className="w-full justify-between">
                              <span className="text-sm font-normal">
                                {getWeekdayOccurrenceStringFromTerminWeekdayOccurrence(
                                  enumValue,
                                  date,
                                  repetition,
                                )}
                              </span>
                              {isSelected && (
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className="text-primary-blue-600"
                                />
                              )}
                            </Button>
                            {idx <
                              Object.values(TerminWeekdayOccurrence).length -
                                1 && (
                              <div className="border-b border-neutrals-300" />
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                </ScrollArea>
              </DrawerContent>
            </Drawer>
          </div>
        );
      }}
    </form.Subscribe>
  );
}
